import {
  createApiRef,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';

import { PullRequestRecord } from '../types';

export interface WatchtowerPullRequestApi {
  getPullRequestsNeedsReview(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsOpened(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsApproved(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsRecentlyMerged(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsReturnedToUser(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsWaitingForAuthor(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }>;
  getPullRequestsNeedsReviewCount(userId: string): Promise<{ count: number }>;
  getPullRequestsOpenedCount(userId: string): Promise<{ count: number }>;
  getPullRequestsApprovedCount(userId: string): Promise<{ count: number }>;
  getPullRequestsRecentlyMergedCount(
    userId: string,
  ): Promise<{ count: number }>;
  getPullRequestsReturnedToUserCount(
    userId: string,
  ): Promise<{ count: number }>;
  getPullRequestsWaitingForAuthorCount(
    userId: string,
  ): Promise<{ count: number }>;
}

export const WatchtowerPullRequestApiRef =
  createApiRef<WatchtowerPullRequestApi>({
    id: 'plugin.pull-requests.watchtower',
  });

export class WatchtowerPRNeedsReviewApiClient
  implements WatchtowerPullRequestApi
{
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly identityApi: IdentityApi,
  ) {}

  async getPullRequestsNeedsReview(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/review`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch pull requests needs review: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsOpened(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/opened`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch pull requests opened: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsApproved(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/approved`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch approved pull requests: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsRecentlyMerged(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/merged`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch recently merged pull requests: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsReturnedToUser(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/returned`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch returned pull requests: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsWaitingForAuthor(
    userId: string,
    page: number,
    pageSize: number,
  ): Promise<{
    data: PullRequestRecord[];
    pagination: { total: number };
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/waiting-author`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, page, pageSize }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch returned pull requests: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsNeedsReviewCount(
    userId: string,
  ): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/review/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch pull requests needs review count: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsOpenedCount(userId: string): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/opened/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch pull requests opened count: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsApprovedCount(
    userId: string,
  ): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/approved/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch approved pull requests count: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsRecentlyMergedCount(
    userId: string,
  ): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/merged/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch recently merged pull requests count: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsReturnedToUserCount(
    userId: string,
  ): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/returned/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch returned pull requests count: ${res.statusText}`,
      );
    }

    return await res.json();
  }

  async getPullRequestsWaitingForAuthorCount(
    userId: string,
  ): Promise<{ count: number }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower');
    const { token } = await this.identityApi.getCredentials();

    const res = await fetch(`${baseUrl}/pullrequests/waiting-author/count`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch waiting for author pull requests count: ${res.statusText}`,
      );
    }

    return await res.json();
  }
}
